import { useEffect, useState } from 'react';
import { useToast } from '../../../components/ToastContext';

const emptyForm = {
  coupon_type: 'CODE',
  title: '',
  description: '',
  code: '',
  card_image_url: '',
  discount_type: 'PERCENT',
  discount_value: 10,
  max_discount_amount: '',
  min_booking_amount: '',
  start_date: '',
  end_date: '',
  usage_limit_total: '',
  usage_limit_per_user: '',
  applies_to: 'ALL',
  is_active: true,
  apply_scope: 'selected',
  property_ids: [],
};

const CouponsPromoTab = ({ accessToken, propertyId, properties = [], listFn, saveFn, deleteFn, uploadFn }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const toast = useToast();
  const propertyMap = new Map(properties.map((p) => [String(p.id), p.title || `Property ${p.id}`]));

  const load = async () => {
    if (!propertyId) return;
    setError('');
    try {
      const rows = await listFn(accessToken, propertyId);
      setItems(rows || []);
    } catch (err) {
      setError(err.message || 'Failed to load coupons');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  useEffect(() => {
    if (editingId || !propertyId) return;
    setForm((prev) => {
      if (prev.apply_scope !== 'selected') return prev;
      if (prev.property_ids?.length) return prev;
      return { ...prev, property_ids: [propertyId] };
    });
  }, [propertyId, editingId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const propertyIds = form.apply_scope === 'selected' ? form.property_ids : [];
      if (form.apply_scope === 'selected' && !propertyIds.length) {
        toast.error('Select at least one property');
        return;
      }
      const payload = { ...form, property_ids: propertyIds };
      if (payload.coupon_type === 'CODE') payload.code = payload.code.toUpperCase();
      if (!editingId) delete payload.id;
      const saved = await saveFn(accessToken, editingId ? { ...payload, id: editingId } : payload);
      setItems((prev) => {
        const filtered = prev.filter((c) => c.id !== saved.id);
        return [saved, ...filtered];
      });
      setForm(emptyForm);
      setEditingId(null);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save coupon');
    }
  };

  const startEdit = (row) => {
    const propertyIds = row.property_ids?.length ? row.property_ids : row.property_id ? [row.property_id] : [];
    setEditingId(row.id);
    setForm({
      ...emptyForm,
      ...row,
      coupon_type: row.coupon_type || 'CODE',
      discount_type: (row.discount_type || 'PERCENT').toUpperCase(),
      code: row.code || '',
      card_image_url: row.card_image_url || row.image_url || '',
      apply_scope: propertyIds.length ? 'selected' : 'all',
      property_ids: propertyIds.map((id) => String(id)),
    });
  };

  const toggleProperty = (id) => {
    setForm((prev) => {
      const next = new Set(prev.property_ids || []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, property_ids: Array.from(next) };
    });
  };

  const remove = async (row) => {
    if (!window.confirm('Delete this coupon?')) return;
    await deleteFn(accessToken, row.id);
    setItems((prev) => prev.filter((c) => c.id !== row.id));
  };

  const handleUpload = async (file) => {
    try {
      const res = await uploadFn(accessToken, file);
      setForm((f) => ({ ...f, card_image_url: res.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <p className="text-lg font-semibold text-slate-900">Coupons / Promo codes</p>
          <p className="text-sm text-slate-600">Code coupons for checkout and card/image coupons for promo banners.</p>
        </div>
        <button className="text-xs text-emerald-700 underline" onClick={load}>
          Refresh
        </button>
      </div>
      {error && <div className="px-4 py-2 text-sm text-red-600">{error}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
        <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-slate-900">{editingId ? 'Edit coupon' : 'Create coupon'}</p>
              <p className="text-xs text-slate-500">CODE requires a code, CARD requires an image.</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
              Active
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Coupon type</span>
              <select
                value={form.coupon_type}
                onChange={(e) => setForm((f) => ({ ...f, coupon_type: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="CODE">Code</option>
                <option value="CARD">Card (image)</option>
              </select>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Code (for CODE type)</span>
              <input
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
                placeholder="WELCOME10"
                disabled={form.coupon_type === 'CARD'}
              />
            </label>
          </div>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Title</span>
            <input
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              placeholder="Summer sale"
            />
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Description</span>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              rows={2}
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Discount</span>
              <div className="flex gap-2">
                <select
                  value={form.discount_type}
                  onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
                  className="rounded-lg border border-slate-200 px-3 py-2"
                >
                  <option value="PERCENT">Percent</option>
                  <option value="FIXED">Fixed</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.discount_value}
                  onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                  className="w-24 rounded-lg border border-slate-200 px-3 py-2"
                />
              </div>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Max discount (optional)</span>
              <input
                type="number"
                min="0"
                value={form.max_discount_amount}
                onChange={(e) => setForm((f) => ({ ...f, max_discount_amount: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Applies to</span>
              <select
                value={form.apply_scope}
                onChange={(e) => setForm((f) => ({ ...f, apply_scope: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="selected">Selected properties</option>
                <option value="all">All properties</option>
              </select>
            </label>
            {form.apply_scope === 'selected' && (
              <div className="text-sm text-slate-700 space-y-1">
                <span className="font-semibold">Properties</span>
                <div className="max-h-32 overflow-auto rounded-lg border border-slate-200 bg-white p-2">
                  {properties.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700 py-1">
                      <input
                        type="checkbox"
                        checked={form.property_ids.includes(String(p.id))}
                        onChange={() => toggleProperty(String(p.id))}
                      />
                      <span>{p.title || `Property ${p.id}`}</span>
                    </label>
                  ))}
                  {!properties.length && <div className="text-xs text-slate-500">No properties found.</div>}
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Date range</span>
              <div className="flex gap-2">
                <input
                  type="date"
                  required
                  value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2"
                />
                <input
                  type="date"
                  required
                  value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2"
                />
              </div>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Min booking amount</span>
              <input
                type="number"
                min="0"
                value={form.min_booking_amount}
                onChange={(e) => setForm((f) => ({ ...f, min_booking_amount: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Usage limits</span>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  placeholder="Total"
                  value={form.usage_limit_total}
                  onChange={(e) => setForm((f) => ({ ...f, usage_limit_total: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2"
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Per user"
                  value={form.usage_limit_per_user}
                  onChange={(e) => setForm((f) => ({ ...f, usage_limit_per_user: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2"
                />
              </div>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Card image (for CARD)</span>
              <div className="space-y-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
                  className="block w-full text-sm"
                />
                {form.card_image_url && (
                  <img src={form.card_image_url} alt="card" className="h-16 rounded border border-slate-200 object-cover" />
                )}
              </div>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
              {editingId ? 'Update coupon' : 'Create coupon'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="text-sm text-slate-600 underline"
              >
                Cancel edit
              </button>
            )}
          </div>
        </form>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <p className="text-lg font-semibold text-slate-900">Coupons</p>
              <p className="text-xs text-slate-500">Toggle on/off and edit existing coupons.</p>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {items.length === 0 && <div className="p-4 text-sm text-slate-600">No coupons yet.</div>}
            {items.map((c) => (
              <div key={c.id} className="flex items-start justify-between px-4 py-3 hover:bg-slate-50">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{c.coupon_type || 'CODE'}</span>
                    <p className="font-semibold text-slate-900">{c.title || c.name || c.code}</p>
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                      {c.discount_type} {c.discount_value}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    {c.start_date} - {c.end_date} {c.code ? `Code: ${c.code}` : ''}
                  </p>
                  {Array.isArray(c.property_ids) && c.property_ids.length > 0 && (
                    <p className="text-xs text-slate-500">
                      Properties: {c.property_ids.map((id) => propertyMap.get(String(id)) || `#${id}`).join(', ')}
                    </p>
                  )}
                  {c.card_image_url && (
                    <img src={c.card_image_url} alt={c.title} className="h-12 rounded border border-slate-200 object-cover" />
                  )}
                  {c.description && <p className="text-sm text-slate-700">{c.description}</p>}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <label className="flex items-center gap-1 text-slate-600">
                    <input
                      type="checkbox"
                      checked={!!c.is_active}
                      onChange={(e) => saveFn(accessToken, { ...c, is_active: e.target.checked }).then(load)}
                    />
                    Active
                  </label>
                  <button className="text-emerald-700 underline" onClick={() => startEdit(c)}>
                    Edit
                  </button>
                  <button className="text-red-600 underline" onClick={() => remove(c)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponsPromoTab;

